import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Badge,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Divider,
  Tooltip,
  LinearProgress,
  Collapse,
  InputAdornment,
  Switch,
  FormControlLabel,
  Autocomplete,
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineDot,
  TimelineConnector,
  TimelineContent
} from '@mui/lab';
import {
  List as WaitingListIcon,
  Person as PersonIcon,
  Hotel as SiteIcon,
  Schedule as ScheduleIcon,
  TrendingUp as PriorityIcon,
  Notifications as NotificationIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Pending as PendingIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  Phone as PhoneIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  StarBorder as StarIcon,
  Star as StarFilledIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  Timer as TimerIcon,
  Analytics as AnalyticsIcon,
  Assignment as OfferIcon,
  History as HistoryIcon,
  Warning as AlertIcon,
  Refresh as RefreshIcon,
  GetApp as ExportIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Group as GroupIcon,
  AttachMoney as MoneyIcon,
  EventNote as EventIcon,
  Chat as ChatIcon,
  Settings as SettingsIcon,
  Send as SendIcon,
  PlayArrow as StartIcon,
  CheckCircle as CompleteIcon,
  Sync as SyncIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { styled } from '@mui/material/styles';
import api from '../services/api';

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  transition: 'all 0.3s ease-in-out',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  },
}));

const PriorityChip = styled(Chip)<{ priority: string }>(({ theme, priority }) => {
  const getColor = () => {
    switch (priority) {
      case 'emergency': return { bg: '#f44336', color: '#fff' };
      case 'premium': return { bg: '#9c27b0', color: '#fff' };
      case 'returning': return { bg: '#ff9800', color: '#fff' };
      case 'member': return { bg: '#2196f3', color: '#fff' };
      default: return { bg: '#e0e0e0', color: '#000' };
    }
  };

  const colors = getColor();
  return {
    backgroundColor: colors.bg,
    color: colors.color,
    fontWeight: 'bold',
  };
});

const StatusChip = styled(Chip)<{ status: string }>(({ theme, status }) => {
  const getColor = () => {
    switch (status) {
      case 'active': return { bg: '#4caf50', color: '#fff' };
      case 'notified': return { bg: '#ff9800', color: '#fff' };
      case 'confirmed': return { bg: '#2196f3', color: '#fff' };
      case 'fulfilled': return { bg: '#8bc34a', color: '#fff' };
      case 'expired': return { bg: '#9e9e9e', color: '#fff' };
      case 'cancelled': return { bg: '#f44336', color: '#fff' };
      default: return { bg: '#e0e0e0', color: '#000' };
    }
  };

  const colors = getColor();
  return {
    backgroundColor: colors.bg,
    color: colors.color,
  };
});

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`waiting-list-tabpanel-${index}`}
      aria-labelledby={`waiting-list-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export default function WaitingListPage() {
  const [currentTab, setCurrentTab] = useState(0);
  const [waitingListEntries, setWaitingListEntries] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<any>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    status: 'active',
    siteType: 'all',
    priority: 'all',
    startDate: null as Date | null,
    endDate: null as Date | null,
    search: ''
  });

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalEntries, setTotalEntries] = useState(0);

  // Dashboard stats
  const [dashboardStats, setDashboardStats] = useState({
    activeCount: 0,
    todayAdded: 0,
    offersCount: 0,
    expiringSoon: 0,
    conversionRate: 0,
    averageWaitTime: []
  });

  // New entry form
  const [newEntry, setNewEntry] = useState({
    guest: null as any,
    requestedDates: {
      checkIn: null as Date | null,
      checkOut: null as Date | null,
      flexible: false,
      flexibilityDays: 0
    },
    siteRequirements: {
      siteType: 'any',
      specificSites: [],
      amenities: { required: [], preferred: [] },
      accessibility: { required: false },
      size: {},
      utilities: { electric: { required: false }, water: false, sewer: false },
      location: {}
    },
    partyDetails: {
      adults: 1,
      children: 0,
      pets: { count: 0, types: [], sizes: [] },
      vehicles: {}
    },
    priority: { level: 'standard' },
    notifications: {
      preferences: { email: true, sms: false, phone: false },
      schedule: { immediate: true, weekly: false }
    },
    expirationDate: null
  });

  useEffect(() => {
    fetchDashboardStats();
    fetchWaitingListEntries();
  }, [currentTab, filters, page, rowsPerPage]);

  const fetchDashboardStats = async () => {
    try {
      const response = await api.get('/waiting-list/dashboard/summary');
      setDashboardStats(response.data);
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    }
  };

  const fetchWaitingListEntries = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        status: filters.status,
        siteType: filters.siteType,
        priority: filters.priority,
        search: filters.search,
        page: (page + 1).toString(),
        limit: rowsPerPage.toString()
      });
      
      if (filters.startDate) {
        params.append('startDate', filters.startDate.toISOString());
      }
      if (filters.endDate) {
        params.append('endDate', filters.endDate.toISOString());
      }

      const response = await api.get('/waiting-list', { params: Object.fromEntries(params) });
      setWaitingListEntries(response.data.entries);
      setTotalEntries(response.data.pagination.total);
    } catch (error) {
      console.error('Error fetching waiting list entries:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
    setPage(0);
  };

  const handleFilterChange = (field: string, value: any) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(0);
  };

  const handleEntryClick = async (entry: any) => {
    try {
      const response = await api.get(`/waiting-list/${entry._id}`);
      setSelectedEntry(response.data);
      setDetailsOpen(true);
    } catch (error) {
      console.error('Error fetching entry details:', error);
    }
  };

  const handleCheckMatches = async (entryId: string | null = null) => {
    try {
      const response = await api.post('/waiting-list/check-matches', { entryId });
      const results = response.data;
      alert(`Checked ${results.entriesChecked} entries, found ${results.matchesFound} matches, sent ${results.offersSent} offers`);
      fetchWaitingListEntries();
    } catch (error) {
      console.error('Error checking matches:', error);
    }
  };

  const renderDashboard = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6} md={3}>
        <StyledCard>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                <WaitingListIcon />
              </Avatar>
              <Typography variant="h6">Active Entries</Typography>
            </Box>
            <Typography variant="h4" color="primary">
              {dashboardStats.activeCount}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Currently on waiting list
            </Typography>
          </CardContent>
        </StyledCard>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <StyledCard>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar sx={{ bgcolor: 'success.main', mr: 2 }}>
                <AddIcon />
              </Avatar>
              <Typography variant="h6">Added Today</Typography>
            </Box>
            <Typography variant="h4" color="success.main">
              {dashboardStats.todayAdded}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              New entries today
            </Typography>
          </CardContent>
        </StyledCard>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <StyledCard>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar sx={{ bgcolor: 'warning.main', mr: 2 }}>
                <OfferIcon />
              </Avatar>
              <Typography variant="h6">Pending Offers</Typography>
            </Box>
            <Typography variant="h4" color="warning.main">
              {dashboardStats.offersCount}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Awaiting response
            </Typography>
          </CardContent>
        </StyledCard>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <StyledCard>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar sx={{ bgcolor: 'error.main', mr: 2 }}>
                <AlertIcon />
              </Avatar>
              <Typography variant="h6">Expiring Soon</Typography>
            </Box>
            <Typography variant="h4" color="error.main">
              {dashboardStats.expiringSoon}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Within 7 days
            </Typography>
          </CardContent>
        </StyledCard>
      </Grid>

      <Grid item xs={12} md={8}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Recent Activity
          </Typography>
          <Timeline>
            {waitingListEntries.slice(0, 5).map((entry: any, index: number) => (
              <TimelineItem key={entry._id}>
                <TimelineSeparator>
                  <TimelineDot color={entry.status === 'active' ? 'primary' : 'grey'}>
                    <PersonIcon />
                  </TimelineDot>
                  {index < 4 && <TimelineConnector />}
                </TimelineSeparator>
                <TimelineContent>
                  <Typography variant="subtitle2">
                    {entry.guest?.firstName} {entry.guest?.lastName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {entry.requestedDates?.checkIn ? 
                      `Requested ${new Date(entry.requestedDates.checkIn).toLocaleDateString()} - ${new Date(entry.requestedDates.checkOut).toLocaleDateString()}` :
                      'No dates specified'
                    }
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Position #{entry.analytics?.position?.current || 'N/A'} • {entry.priority?.level} priority
                  </Typography>
                </TimelineContent>
              </TimelineItem>
            ))}
          </Timeline>
        </Paper>
      </Grid>

      <Grid item xs={12} md={4}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Performance Metrics
          </Typography>
          <Stack spacing={2}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Conversion Rate
              </Typography>
              <Typography variant="h5" color="success.main">
                {dashboardStats.conversionRate.toFixed(1)}%
              </Typography>
            </Box>
            <Divider />
            <Box>
              <Typography variant="body2" color="text.secondary">
                Average Wait Time by Type
              </Typography>
              {dashboardStats.averageWaitTime.map((item: any) => (
                <Box key={item._id} sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    {item._id}: {item.avgWaitTime?.toFixed(1) || 0} days
                  </Typography>
                </Box>
              ))}
            </Box>
          </Stack>
        </Paper>
      </Grid>
    </Grid>
  );

  const renderWaitingListTable = () => (
    <Paper>
      <Box sx={{ p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              placeholder="Search guests..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="notified">Notified</MenuItem>
                <MenuItem value="confirmed">Confirmed</MenuItem>
                <MenuItem value="fulfilled">Fulfilled</MenuItem>
                <MenuItem value="expired">Expired</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Site Type</InputLabel>
              <Select
                value={filters.siteType}
                onChange={(e) => handleFilterChange('siteType', e.target.value)}
                label="Site Type"
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="rv">RV</MenuItem>
                <MenuItem value="tent">Tent</MenuItem>
                <MenuItem value="cabin">Cabin</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={filters.priority}
                onChange={(e) => handleFilterChange('priority', e.target.value)}
                label="Priority"
              >
                <MenuItem value="all">All Priorities</MenuItem>
                <MenuItem value="standard">Standard</MenuItem>
                <MenuItem value="member">Member</MenuItem>
                <MenuItem value="returning">Returning</MenuItem>
                <MenuItem value="premium">Premium</MenuItem>
                <MenuItem value="emergency">Emergency</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={3}>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={() => fetchWaitingListEntries()}
              >
                Refresh
              </Button>
              <Button
                variant="outlined"
                startIcon={<SyncIcon />}
                onClick={() => handleCheckMatches()}
              >
                Check All
              </Button>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setAddDialogOpen(true)}
              >
                Add Entry
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Guest</TableCell>
              <TableCell>Requested Dates</TableCell>
              <TableCell>Site Type</TableCell>
              <TableCell>Position</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Wait Time</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8}>
                  <LinearProgress />
                </TableCell>
              </TableRow>
            ) : (
              waitingListEntries.map((entry: any) => (
                <TableRow 
                  key={entry._id}
                  hover
                  onClick={() => handleEntryClick(entry)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar sx={{ mr: 2, width: 32, height: 32 }}>
                        {entry.guest?.firstName?.[0] || 'G'}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2">
                          {entry.guest?.firstName} {entry.guest?.lastName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {entry.guest?.email}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {entry.requestedDates?.checkIn ? 
                        `${new Date(entry.requestedDates.checkIn).toLocaleDateString()} - ${new Date(entry.requestedDates.checkOut).toLocaleDateString()}` :
                        'Not specified'
                      }
                    </Typography>
                    {entry.requestedDates?.flexible && (
                      <Chip size="small" label={`±${entry.requestedDates.flexibilityDays}d`} variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      size="small" 
                      label={entry.siteRequirements?.siteType || 'Any'} 
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="h6" color="primary">
                      #{entry.analytics?.position?.current || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <PriorityChip 
                      size="small" 
                      priority={entry.priority?.level || 'standard'}
                      label={entry.priority?.level || 'Standard'} 
                    />
                  </TableCell>
                  <TableCell>
                    <StatusChip 
                      size="small" 
                      status={entry.status}
                      label={entry.status} 
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {entry.daysWaiting || 0} days
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="View Details">
                        <IconButton size="small">
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Check Matches">
                        <IconButton 
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCheckMatches(entry._id);
                          }}
                        >
                          <SyncIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={totalEntries}
        page={page}
        onPageChange={(event, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />
    </Paper>
  );

  const renderAnalytics = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Alert severity="info">
          Analytics dashboard coming soon! Will include conversion rates, wait time trends, and performance metrics.
        </Alert>
      </Grid>
    </Grid>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Waiting List Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage guest waiting list with automated matching and notifications
        </Typography>
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={currentTab} onChange={handleTabChange}>
          <Tab label="Dashboard" />
          <Tab label="Active Entries" />
          <Tab label="Analytics" />
        </Tabs>
      </Paper>

      <TabPanel value={currentTab} index={0}>
        {renderDashboard()}
      </TabPanel>

      <TabPanel value={currentTab} index={1}>
        {renderWaitingListTable()}
      </TabPanel>

      <TabPanel value={currentTab} index={2}>
        {renderAnalytics()}
      </TabPanel>

      {/* Entry Details Dialog */}
      <Dialog 
        open={detailsOpen} 
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Waiting List Entry Details
        </DialogTitle>
        <DialogContent>
          {selectedEntry && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>Guest Information</Typography>
                <Typography><strong>Name:</strong> {selectedEntry.guest?.firstName} {selectedEntry.guest?.lastName}</Typography>
                <Typography><strong>Email:</strong> {selectedEntry.guest?.email}</Typography>
                <Typography><strong>Phone:</strong> {selectedEntry.guest?.phone}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>Entry Details</Typography>
                <Typography><strong>Entry ID:</strong> {selectedEntry.entryId}</Typography>
                <Typography><strong>Status:</strong> {selectedEntry.status}</Typography>
                <Typography><strong>Priority:</strong> {selectedEntry.priority?.level} (Score: {selectedEntry.priority?.score})</Typography>
                <Typography><strong>Position:</strong> #{selectedEntry.analytics?.position?.current}</Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Add Entry Dialog */}
      <Dialog 
        open={addDialogOpen} 
        onClose={() => setAddDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add New Waiting List Entry</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Add new entry form will be implemented with full guest and requirement details.
          </Alert>
          <TextField
            fullWidth
            label="Guest Email"
            type="email"
            margin="normal"
            helperText="Enter guest email to lookup or create new guest"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled>Add Entry</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}